import React, { useRef, useState } from 'react';
import RefreshCheckApi from '../../../communication_system/axios_apis/refresh_check/refresh_check_api';

export const CORPORATE_SCHEDULE_ENDPOINT = '/adminRequest/payment/manual-offline-schedule-complete';
export const CORPORATE_SCHEDULE_REASON = '법인계좌 별도입금 확인';
const REGISTERED_ONSITE_REASON = '현장결제 수령 확인';

export const corporateScheduleEndpoint = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return `http://127.0.0.1:13294${CORPORATE_SCHEDULE_ENDPOINT}`;
    if (hostname === 'admin.senor.co.kr') return `https://senor.co.kr${CORPORATE_SCHEDULE_ENDPOINT}`;
    return `https://develop.senor.co.kr${CORPORATE_SCHEDULE_ENDPOINT}`;
};

const attemptStorageKey = ({ adminId, targetUserId, gatheringId, contentsId, onsiteRegistration, expectedPayId }) =>
    onsiteRegistration
        ? `tid1738:registered-onsite:${Number(adminId)}:${Number(targetUserId)}:${Number(gatheringId)}:${Number(contentsId)}:${Number(expectedPayId)}`
        : `tid1694:corporate-schedule:${Number(adminId)}:${Number(targetUserId)}:${Number(gatheringId)}:${Number(contentsId)}`;

export const createCorporateScheduleIdempotencyKey = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `corp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
};

const safeParseAttempt = (raw, onsiteRegistration = false) => {
    try {
        const parsed = JSON.parse(raw);
        const payload = parsed?.payload;
        if (!payload || typeof parsed.key !== 'string' || parsed.key.length < 8) return null;
        const numeric = ['target_user_id', 'gathering_id', 'contents_id', 'expected_amount', 'expected_pay_id'];
        if (numeric.some((field) => !Number.isInteger(payload[field]) || payload[field] < 0)) return null;
        if (payload.action !== (onsiteRegistration ? 'complete_registered' : 'complete') || payload.reason !== (onsiteRegistration ? REGISTERED_ONSITE_REASON : CORPORATE_SCHEDULE_REASON)) return null;
        if (onsiteRegistration && payload.expected_pay_id <= 0) return null;
        return { key: parsed.key, payload };
    } catch (_) {
        return null;
    }
};

export const loadCorporateScheduleAttempt = (identity) => safeParseAttempt(sessionStorage.getItem(attemptStorageKey(identity)), identity.onsiteRegistration);

export const saveCorporateScheduleAttempt = (identity, attempt) => {
    sessionStorage.setItem(attemptStorageKey(identity), JSON.stringify({ key: attempt.key, payload: attempt.payload }));
};

export const clearCorporateScheduleAttempt = (identity) => sessionStorage.removeItem(attemptStorageKey(identity));

export const buildCorporateSchedulePayload = (preview, onsiteRegistration = false) => ({
    action: onsiteRegistration ? 'complete_registered' : 'complete',
    target_user_id: Number(preview.target_user_id),
    gathering_id: Number(preview.gathering_id),
    contents_id: Number(preview.contents_id),
    expected_amount: Number(preview.expected_amount),
    expected_pay_id: Number(preview.expected_pay_id),
    reason: onsiteRegistration ? REGISTERED_ONSITE_REASON : CORPORATE_SCHEDULE_REASON,
});

export const isCorporateScheduleCompletion = (response) => {
    const envelope = response?.data;
    return envelope?.ok === true && Number.isInteger(Number(envelope?.data?.pay_id)) && Number(envelope.data.pay_id) > 0 && Number(envelope?.data?.pay_status) === 2;
};

const previewMessage = (preview, onsiteRegistration) => {
    const source = preview.expected_pay_id > 0 ? `기존 현장결제 #${preview.expected_pay_id}` : '새 결제내역';
    return [
        `회원: ${preview.user_name} (#${preview.target_user_id})`,
        `모임: ${preview.gathering_name} (#${preview.gathering_id})`,
        `일정: ${preview.title} (#${preview.contents_id})`,
        `금액: ${Number(preview.expected_amount).toLocaleString('ko-KR')}원`,
        `처리 대상: ${source}`,
        '',
        onsiteRegistration ? '현장결제 수령을 확인했으며 선택한 결제를 결제완료로 처리할까요?' : '법인계좌 입금을 확인했으며 결제완료로 처리할까요?',
    ].join('\n');
};

function CorporateSchedulePayment({ adminId, targetUserId, gatheringId, contentsId, expectedPayId, onsiteRegistration = false, refresh, close }) {
    const pending = useRef(false);
    const [loading, setLoading] = useState(false);
    const identity = { adminId, targetUserId, gatheringId, contentsId, onsiteRegistration, expectedPayId };
    const detailMode = expectedPayId !== undefined;
    const matchesDetail = (source) => Number(source.target_user_id) === Number(targetUserId)
        && Number(source.gathering_id) === Number(gatheringId)
        && Number(source.contents_id) === Number(contentsId)
        && Number(source.expected_pay_id) === Number(expectedPayId);

    const finishAttempt = async (attempt) => {
        const response = await RefreshCheckApi.post(corporateScheduleEndpoint(), attempt.payload, {
            headers: { 'Idempotency-Key': attempt.key },
        });
        if (!isCorporateScheduleCompletion(response) || (detailMode && Number(response.data.data.pay_id) !== Number(expectedPayId))) {
            throw new Error('결제완료 결과를 확인하지 못했습니다. 같은 요청으로 다시 시도해 주세요.');
        }
        clearCorporateScheduleAttempt(identity);
        refresh?.();
        close?.();
        window.alert(onsiteRegistration ? '현장결제 결제완료 처리가 완료되었습니다.' : '별도입금 결제완료 처리가 완료되었습니다.');
    };

    const onComplete = async () => {
        if (pending.current) return;
        const ids = [adminId, targetUserId, gatheringId, contentsId].map(Number);
        if (ids.some((id) => !Number.isInteger(id) || id <= 0) || (detailMode && (!Number.isInteger(Number(expectedPayId)) || Number(expectedPayId) <= 0))) {
            window.alert('회원, 모임, 일정을 먼저 선택해 주세요.');
            return;
        }
        pending.current = true;
        setLoading(true);
        try {
            const retained = loadCorporateScheduleAttempt(identity);
            if (retained) {
                if (detailMode && !matchesDetail(retained.payload)) throw new Error('이전에 처리한 요청이 선택한 결제와 다릅니다. 기존 요청의 결제 상태를 먼저 확인해 주세요.');
                await finishAttempt(retained);
                return;
            }
            const response = await RefreshCheckApi.post(corporateScheduleEndpoint(), {
                action: onsiteRegistration ? 'preview_registered' : 'preview', target_user_id: ids[1], gathering_id: ids[2], contents_id: ids[3],
                ...(onsiteRegistration ? { expected_pay_id: Number(expectedPayId) } : {}),
            });
            const preview = response?.data?.ok === true ? response?.data?.data : null;
            if (!preview || !['new', 'pending', 'completed', 'blocked'].includes(preview.state)) {
                throw new Error('처리 대상 확인 결과가 올바르지 않습니다.');
            }
            if (detailMode && (preview.state !== 'pending' || !matchesDetail(preview))) {
                throw new Error('선택한 현장결제와 처리 대상이 다릅니다. 결제 상세를 다시 확인해 주세요.');
            }
            if (preview.state === 'completed' || preview.state === 'blocked') {
                window.alert(preview.message || (preview.state === 'completed' ? '이미 결제완료된 회원입니다.' : '현재 상태에서는 처리할 수 없습니다.'));
                return;
            }
            if (!window.confirm(previewMessage(preview, onsiteRegistration))) return;
            const attempt = { key: createCorporateScheduleIdempotencyKey(), payload: buildCorporateSchedulePayload(preview, onsiteRegistration) };
            saveCorporateScheduleAttempt(identity, attempt);
            await finishAttempt(attempt);
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || '별도입금 결제완료 요청 결과를 확인하지 못했습니다.';
            if (error?.response?.status === 409 && error?.response?.data?.error_code === 'STALE_PREVIEW_NOT_APPLIED') {
                clearCorporateScheduleAttempt(identity);
                window.alert(`처리 대상이 변경되어 결제완료로 반영되지 않았습니다. 다시 미리보기를 확인해 주세요.\n${message}`);
                return;
            }
            window.alert(`처리 실패: ${message}`);
        } finally {
            pending.current = false;
            setLoading(false);
        }
    };

    return (
        <div style={{ marginTop: '16px', padding: '12px', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
            <div style={{ marginBottom: '8px', lineHeight: 1.5 }}>
                {onsiteRegistration ? '현장결제를 실제 수령한 뒤 결제완료로 처리해 주세요.' : '법인계좌 입금을 먼저 확인한 뒤 사용해 주세요. 이 기능은 카드·가상계좌 승인이나 환불을 실행하지 않습니다. 진행 중인 가상계좌 결제가 있으면 기존 취소 처리를 완료한 후 다시 확인해 주세요.'}
            </div>
            <button type="button" onClick={onComplete} disabled={loading}>
                {loading ? '처리 중...' : onsiteRegistration ? '결제완료 처리' : '별도입금 결제완료 처리'}
            </button>
        </div>
    );
}

export default CorporateSchedulePayment;
